/**
 * Secure Storage Service
 * Provides encrypted storage for sensitive data with automatic expiration
 */

import { encryptionService } from './encryption';
import { auditLogger } from './auditLogger';

export interface SecureStorageConfig {
  encryption: boolean;
  prefix: string;
  defaultExpiry: number; // milliseconds
}

export interface StorageItem<T = unknown> {
  value: T;
  encrypted: boolean;
  createdAt: string;
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}

class SecureStorage {
  private config: SecureStorageConfig;

  constructor(config?: Partial<SecureStorageConfig>) {
    this.config = {
      encryption: true,
      prefix: 'ww_secure_',
      defaultExpiry: 3600000, // 1 hour
      ...config,
    };
  }

  /**
   * Store a value securely
   */
  async set<T>(
    key: string,
    value: T,
    options?: {
      encrypt?: boolean;
      expiresIn?: number;
      metadata?: Record<string, unknown>;
    }
  ): Promise<void> {
    const encrypt = options?.encrypt ?? this.config.encryption;
    const expiresIn = options?.expiresIn ?? this.config.defaultExpiry;

    const now = new Date();
    const expiresAt = expiresIn ? new Date(now.getTime() + expiresIn) : undefined;

    let storedValue: unknown = value;

    // Encrypt if enabled
    if (encrypt && typeof value === 'string') {
      storedValue = await encryptionService.encrypt(value);
    } else if (encrypt && typeof value === 'object' && value !== null) {
      const json = JSON.stringify(value);
      storedValue = await encryptionService.encrypt(json);
    }

    const item: StorageItem<unknown> = {
      value: storedValue,
      encrypted: encrypt,
      createdAt: now.toISOString(),
      expiresAt: expiresAt?.toISOString(),
      metadata: options?.metadata,
    };

    const storageKey = this.getStorageKey(key);
    sessionStorage.setItem(storageKey, JSON.stringify(item));

    await auditLogger.logSecurityEvent('SECURE_STORAGE_SET', 'success', {
      key,
      encrypted: encrypt,
    });
  }

  /**
   * Retrieve a value from secure storage
   */
  async get<T>(key: string): Promise<T | null> {
    const storageKey = this.getStorageKey(key);
    const itemJson = sessionStorage.getItem(storageKey);

    if (!itemJson) {
      return null;
    }

    try {
      const item: StorageItem = JSON.parse(itemJson);

      // Check expiration
      if (item.expiresAt && new Date() > new Date(item.expiresAt)) {
        await this.remove(key);
        return null;
      }

      let value = item.value;

      // Decrypt if encrypted
      if (item.encrypted && typeof item.value === 'string') {
        const decrypted = await encryptionService.decrypt(item.value);

        // Try to parse as JSON
        try {
          value = JSON.parse(decrypted);
        } catch {
          value = decrypted;
        }
      }

      return value as T;
    } catch (error) {
      console.error('[SECURE_STORAGE] Failed to retrieve value:', error);
      await auditLogger.logSecurityEvent('SECURE_STORAGE_GET_FAILED', 'failure', {
        key,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return null;
    }
  }

  /**
   * Remove a value from storage
   */
  async remove(key: string): Promise<void> {
    const storageKey = this.getStorageKey(key);
    sessionStorage.removeItem(storageKey);

    await auditLogger.logSecurityEvent('SECURE_STORAGE_REMOVE', 'success', {
      key,
    });
  }

  /**
   * Check if a key exists and is not expired
   */
  async has(key: string): Promise<boolean> {
    const value = await this.get(key);
    return value !== null;
  }

  /**
   * Clear all secure storage items
   */
  async clear(): Promise<void> {
    const keys = this.getAllKeys();

    for (const key of keys) {
      sessionStorage.removeItem(key);
    }

    await auditLogger.logSecurityEvent('SECURE_STORAGE_CLEAR', 'success', {
      count: keys.length,
    });
  }

  /**
   * Get all keys in secure storage
   */
  getAllKeys(): string[] {
    const keys: string[] = [];

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (key && key.startsWith(this.config.prefix)) {
        keys.push(key);
      }
    }

    return keys;
  }

  /**
   * Clean up expired items
   */
  async cleanup(): Promise<number> {
    let removedCount = 0;

    const keys = this.getAllKeys();

    for (const storageKey of keys) {
      const itemJson = sessionStorage.getItem(storageKey);

      if (!itemJson) {
        continue;
      }

      try {
        const item: StorageItem = JSON.parse(itemJson);

        if (item.expiresAt && new Date() > new Date(item.expiresAt)) {
          sessionStorage.removeItem(storageKey);
          removedCount++;
        }
      } catch (error) {
        console.error('[SECURE_STORAGE] Error during cleanup:', error);
      }
    }

    if (removedCount > 0) {
      await auditLogger.logSecurityEvent('SECURE_STORAGE_CLEANUP', 'success', {
        removedCount,
      });
    }

    return removedCount;
  }

  /**
   * Get storage key with prefix
   */
  private getStorageKey(key: string): string {
    return `${this.config.prefix}${key}`;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<SecureStorageConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Singleton instance
let secureStorageInstance: SecureStorage | null = null;

export function getSecureStorage(
  config?: Partial<SecureStorageConfig>
): SecureStorage {
  if (!secureStorageInstance) {
    secureStorageInstance = new SecureStorage(config);
  }
  return secureStorageInstance;
}

export const secureStorage = getSecureStorage();

/**
 * Utility: Store form draft securely
 */
export async function storeFormDraft(
  formId: string,
  data: Record<string, unknown>
): Promise<void> {
  await secureStorage.set(`form_draft_${formId}`, data, {
    encrypt: true,
    expiresIn: 7 * 24 * 60 * 60 * 1000, // 7 days
    metadata: {
      formId,
      savedAt: new Date().toISOString(),
    },
  });
}

/**
 * Utility: Retrieve form draft
 */
export async function getFormDraft<T extends Record<string, unknown>>(
  formId: string
): Promise<T | null> {
  return await secureStorage.get<T>(`form_draft_${formId}`);
}

/**
 * Utility: Clear form draft
 */
export async function clearFormDraft(formId: string): Promise<void> {
  await secureStorage.remove(`form_draft_${formId}`);
}
